import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/auth";
import NexWordmark from "@/components/NexWordmark";
import { LIFE_LOGO } from "@/lib/assets";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Check your email for a reset link.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
      <div className="absolute inset-0 blueprint-bg opacity-40 pointer-events-none" />
      <div className="w-full max-w-md relative">
        <Link to="/" className="flex items-center justify-center gap-4 mb-10">
          <img src={LIFE_LOGO} alt="LIFE" className="h-14 w-14 object-contain" />
          <div className="w-px h-12 bg-nex-border" />
          <div>
            <NexWordmark size="text-3xl" />
            <div className="mono-body text-[10px] tracking-[0.28em] uppercase mt-1">Owner Console</div>
          </div>
        </Link>

        {sent ? (
          <div data-testid="forgot-success" className="card-industrial corner-tick p-8 md:p-10 text-center">
            <CheckCircle2 size={40} className="text-nex-gold mx-auto mb-4" />
            <h1 className="serif-head text-2xl mb-3">Check your inbox</h1>
            <p className="text-nex-chrome mb-6">
              If <span className="text-nex-silver">{email}</span> is registered, a reset link has been sent. The link expires in 30 minutes.
            </p>
            <Link to="/admin/login" className="tick-btn-secondary inline-flex">
              <ArrowLeft size={16} /> Back to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="card-industrial corner-tick p-8 md:p-10 space-y-6">
            <div className="text-center">
              <Mail size={22} className="text-nex-gold mx-auto mb-3" />
              <div className="mono-label mb-2">// PASSWORD RECOVERY</div>
              <h1 className="serif-head text-3xl">Forgot password?</h1>
              <p className="text-nex-chrome text-sm mt-3">
                Enter your admin email and we&apos;ll send you a reset link.
              </p>
            </div>

            <div>
              <label className="mono-label block mb-2">Email</label>
              <input
                data-testid="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-industrial"
                placeholder="you@nexengines.com"
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit"
              className="tick-btn-primary w-full justify-center disabled:opacity-50"
            >
              {busy ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <>Send Reset Link <Mail size={16} /></>}
            </button>

            <Link to="/admin/login" className="block text-center mono-body text-[11px] text-nex-chrome hover:text-nex-gold">
              ← Back to Sign In
            </Link>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
